import { toast } from 'sonner';
import { type ChangeEvent, useRef } from 'react';
import { Camera, Inbox, Mic } from 'lucide-react';
import { useNavigate } from '@tanstack/react-router';

import { useOrgDateFormat } from '@/lib/timezone';
import { MAX_FILE_SIZE_BYTES, MAX_FILE_SIZE_MB } from '../constants';
import { useUploadDocument, useUploadedDocuments } from '../api/uploaded-documents-api';

/**
 * Personal-edition Home hero: two mobile-friendly action tiles above the
 * household member list, plus an Inbox row.
 *
 *  - Quick Start → /quick-start (record a visit).
 *  - Scan       → opens the rear camera (mobile) or a file picker (desktop),
 *                 uploads the captured image to the Inbox, and routes the
 *                 user there to adjudicate it onto a household member.
 *  - Inbox      → /documents, with a count of uploads awaiting assignment.
 */
export function PersonalHomeTiles() {
  const navigate = useNavigate();
  const cameraInputRef = useRef<HTMLInputElement>(null);
  const upload = useUploadDocument();
  const { today } = useOrgDateFormat();

  // Same params as the header PersonalInboxButton → shared query cache entry.
  const { data: inboxData } = useUploadedDocuments({ limit: 1, inbox: true });
  const inboxCount = inboxData?.meta.total_items ?? 0;

  const handleCameraFile = async (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    // Reset the input value early so picking the same file again still fires
    // change events, no matter which exit branch we take below.
    e.target.value = '';
    if (!file) return;

    if (!isLikelyImage(file)) {
      toast.error('Please choose an image file.');
      return;
    }
    if (file.size > MAX_FILE_SIZE_BYTES) {
      toast.error(`Image too large. Max ${MAX_FILE_SIZE_MB} MB.`);
      return;
    }

    const baseName = file.name.replace(/\.[^.]+$/, '') || 'Scan';

    try {
      await upload.mutateAsync({
        file,
        name: baseName,
        document_type: 'other',
        document_date: today(),
      });
      toast.success('Photo uploaded — it will appear in your Inbox.');
      navigate({ to: '/documents' });
    } catch {
      toast.error('Upload failed. Please try again.');
    }
  };

  return (
    <div className="flex flex-col gap-3">
      <div className="grid grid-cols-2 gap-3">
        <button
          type="button"
          className="group flex aspect-square flex-col items-center justify-center gap-3 rounded-2xl border border-border/60 bg-card p-6 text-center transition hover:bg-accent active:scale-[0.98]"
          onClick={() => navigate({ to: '/quick-start' })}>
          <Mic className="h-10 w-10 text-primary" />
          <div>
            <p className="font-semibold">Quick Start</p>
            <p className="text-xs text-muted-foreground">Record a visit</p>
          </div>
        </button>

        <button
          type="button"
          disabled={upload.isPending}
          className="group flex aspect-square flex-col items-center justify-center gap-3 rounded-2xl border border-border/60 bg-card p-6 text-center transition hover:bg-accent active:scale-[0.98] disabled:cursor-not-allowed disabled:opacity-60"
          onClick={() => cameraInputRef.current?.click()}>
          <Camera className="h-10 w-10 text-primary" />
          <div>
            <p className="font-semibold">{upload.isPending ? 'Uploading…' : 'Scan'}</p>
            <p className="text-xs text-muted-foreground">Upload from camera</p>
          </div>
        </button>
      </div>

      <button
        type="button"
        className="flex items-center gap-3 rounded-2xl border border-border/60 bg-card p-4 text-left transition hover:bg-accent active:scale-[0.99]"
        onClick={() => navigate({ to: '/documents' })}>
        <Inbox className={`h-6 w-6 ${inboxCount > 0 ? 'text-primary' : 'text-muted-foreground'}`} />
        <div className="min-w-0 flex-1">
          <p className="font-semibold">Inbox</p>
          <p className="text-xs text-muted-foreground">
            {inboxCount > 0 ? `${inboxCount} document${inboxCount === 1 ? '' : 's'} to assign` : 'No documents waiting'}
          </p>
        </div>
        {inboxCount > 0 && (
          <span className="flex h-6 min-w-6 items-center justify-center rounded-full bg-primary px-2 text-xs font-semibold text-primary-foreground">
            {inboxCount}
          </span>
        )}
      </button>

      <input
        ref={cameraInputRef}
        type="file"
        className="hidden"
        capture="environment"
        // `image/*` covers most types, but iOS HEIC/HEIF often reports an
        // empty MIME on the File object — accept the explicit subtypes too
        // so the picker doesn't grey them out.
        accept="image/*,image/heic,image/heif"
        onChange={handleCameraFile}
      />
    </div>
  );
}

const _IMAGE_EXTENSIONS = ['.jpg', '.jpeg', '.png', '.gif', '.webp', '.heic', '.heif'];

/**
 * Loose image check. `file.type` is the source of truth when present, but
 * iOS Safari reports an empty string for HEIC files captured from the
 * camera, so we fall back to the file extension.
 */
function isLikelyImage(file: File): boolean {
  if (file.type && file.type.startsWith('image/')) return true;
  if (file.type) return false;
  const lower = file.name.toLowerCase();
  return _IMAGE_EXTENSIONS.some((ext) => lower.endsWith(ext));
}
